import type { DataItemId } from '@pn/core/domain/data';
import { UnitSystem, isDateString } from '@pn/core/domain/types';
import { convertUnit, isSIUnit, unitToString } from '@pn/core/domain/units';
import { isGeoJSONGeometry } from '@pn/services/utils/geojson';
import {
  isBoolean,
  isNil,
  isNull,
  isNumber,
  isObject,
  isString,
  isUndefined,
} from 'lodash-es';

const DLS_UWI_REGEX =
  /^([A-Za-z0-9]{3})(\d{2})(\d{2})(\d{3})([A-Za-z0-9]{4})(\d{2})$/; // (16 chars)
const NTS_UWI_REGEX =
  /^(2\d{2})([A-Za-z0-9])(\d{3})([A-Za-z0-9])(\d{3})([A-Za-z0-9])(\d{2})(\d{2})$/; // (16 chars, starts with '2')
const API_REGEX = /^(\d{2})(\d{3})(\d{5})(\d{2})(\d{2})$/; // (14 digits)

export const formatId = (arg: DataItemId, dataType: string): string => {
  const id = arg.toString();

  switch (dataType) {
    case 'wells':
      if (DLS_UWI_REGEX.test(id)) {
        return id.replace(DLS_UWI_REGEX, '$1/$2-$3-$4-$5/$6');
      } else if (NTS_UWI_REGEX.test(id)) {
        return id.replace(NTS_UWI_REGEX, '$1/$2-$3-$4/$5-$6-$7/$8');
      }
      break;
    case 'wells_usa':
      if (API_REGEX.test(id)) {
        return id.replace(API_REGEX, '$1-$2-$3-$4-$5');
      }
      break;
  }

  return id;
};

const formattedPatterns = [
  /^[A-Za-z0-9]{3}\/[0-9]{2}-[0-9]{2}-[0-9]{3}-[A-Za-z0-9]{4}\/[0-9]{2}$/, // DLS UWI
  /^2\d{2}\/[A-Za-z0-9]-\d{3}-[A-Za-z0-9]\/[\d]{3}-[A-Za-z0-9]-\d{2}\/[\d]{2}$/, // NTS UWI
  /^\d{2}-\d{3}-\d{5}-\d{2}-\d{2}$/, // API
];

export function isFormattedId(arg: string): boolean {
  return formattedPatterns.some((pattern) => pattern.test(arg));
}

/**
 * Returns the API state code (the first two digits of an API number), or
 * `undefined` if the id is not a well-formed API number.
 */
export function getApiStateCode(arg: DataItemId): string | undefined {
  return API_REGEX.exec(arg.toString())?.[1];
}

export function formatDomainValue(params: {
  value: unknown;
  unitSystem: UnitSystem;
  displaySymbol?: boolean;
}): string {
  const { value, unitSystem, displaySymbol = false } = params;

  if (isNil(value)) {
    return '';
  }

  if (isBoolean(value)) {
    return value ? 'Yes' : 'No';
  } else if (isNumber(value)) {
    return value.toString();
  } else if (isSIUnit(value)) {
    const convertedUnit = convertUnit(value, unitSystem);
    return unitToString(convertedUnit, { displaySymbol });
  } else if (isGeoJSONGeometry(value)) {
    return '[GeoJSON.Geometry]';
  } else if (isObject(value)) {
    console.warn(
      'Plain objects are not supported when formatting domain values. This indicates a problem with your data.',
      value
    );
    return '[Object]';
  } else if (isDateString(value)) {
    return value;
  } else if (isString(value)) {
    return value;
  } else {
    console.error('Bad domain data', value);
    throw new Error(`Unsupported domain value type: ${typeof value}`);
  }
}

export function getRawDomainValue(
  value: unknown
): string | number | boolean | undefined {
  if (
    isUndefined(value) ||
    isBoolean(value) ||
    isNumber(value) ||
    isString(value)
  ) {
    return value;
  }
  if (isNull(value)) {
    return undefined;
  }
  if (isSIUnit(value)) {
    return value.value; // no unit conversion
  }
  if (isGeoJSONGeometry(value)) {
    return '{geojson-geometry}';
  }

  return '{unknown}';
}

export function formatNumberAsMonth(
  number: number,
  format:
    | 'numeric'
    | '2-digit'
    | 'long'
    | 'short'
    | 'narrow'
    | undefined = 'long'
) {
  return new Date(0, number, 0).toLocaleString(undefined, {
    month: format,
  });
}
